import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthenticationService } from '../_services';
import { PartnerReauthService } from '../_services/partner-reauth.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private authenticationService: AuthenticationService,
        private partnerReauth: PartnerReauthService,
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(catchError((err: HttpErrorResponse | any) => {
            if (err && err.status === 401) {
                // Timeclock endpoints use 401 to signal "wrong PIN / password"
                // — NOT a session expiration. Let the caller's own error
                // handler surface the inline message; never nuke the session.
                // Same for /users/authenticate (unlock / login dialogs).
                if (this.isTimeclockRequest(request) || this.isAuthenticateRequest(request) || this.isPinResetRequest(request)) {
                    return throwError(err);
                }
                if (this.isPartnerRequest(request, err) && this.partnerReauth.hasConnectedPartner()) {
                    if (this.partnerReauth.triggerReauth()) {
                        // Don't logout — we're handing the user off to the
                        // partner OAuth screen, not back to /login. The
                        // request is still rejected so the caller stops
                        // spinning, but the redirect is already in flight.
                        return throwError('Partner authentication required — redirecting to re-auth.');
                    }
                }
                // Non-partner 401: existing behavior (DineNGo session
                // expired) — drop the session and bounce to login.
                this.authenticationService.logout();
                location.reload(true);
            }

            const error = (err && err.error && err.error.message) || (err && err.statusText) || err;
            return throwError(error);
        }));
    }

    private isTimeclockRequest(request: HttpRequest<any>): boolean {
        return request.url.includes('/timeclock/');
    }

    /** The authenticate endpoint returns 401 for wrong credentials —
     *  the caller shows an inline error. Never nuke the session for it. */
    private isAuthenticateRequest(request: HttpRequest<any>): boolean {
        return request.url.includes('/users/authenticate');
    }

    private isPinResetRequest(request: HttpRequest<any>): boolean {
        return request.url.includes('/pinReset');
    }

    private isPartnerRequest(_request: HttpRequest<any>, err: HttpErrorResponse | any): boolean {
        const body = err && err.error;
        return !!(body && (body.partner === true || body.partnerAuth === true || body.code === 'PARTNER_AUTH'));
    }
}
